import type {
  CalendarReconcileColumns,
  CalendarReconcileSnapshot,
} from '@blocksuite/affine/data-view';
import { loadCalendarMailboxLinks } from '@blocksuite/affine/data-view';
import { BRAND_DISPLAY_NAME } from '@streamlain/brand';
import { Service } from '@toeverything/infra';

import type { WorkspaceServerService } from '../../cloud';
import type { WorkspaceService } from '../../workspace';
import {
  CALENDAR_VIEW_BACKED_APP_DESCRIPTOR,
  viewBackedAppResourceId,
} from '../view-backed-app';
import {
  fetchViewBackedAppJson,
  type ViewBackedAppFetch,
  ViewBackedAppHttpError,
  ViewBackedAppServerUnavailableError,
} from '../view-backed-app/http';
import type {
  CalendarEventsResponse,
  CalendarInvitationAssignmentsResponse,
  CalendarInvitationResponseResult,
  CalendarInvitationResponseStatus,
  CalendarMailboxAreaTransitionAction,
  CalendarMailboxAreaTransitionPreview,
  CalendarMailboxAreaTransitionResource,
  CalendarMailboxAreaTransitionResult,
  CalendarMailboxLink,
  CalendarMailboxLinkPatch,
  CalendarMailboxLinksResponse,
  CalendarMode,
  CalendarProjectedEventWire,
  CalendarSourceRef,
} from './types';

export class CalendarHttpError extends ViewBackedAppHttpError {
  constructor(status: number, statusText = '', message?: string) {
    super(
      status,
      statusText,
      message ?? `Calendar API request failed with HTTP ${status}`,
      'CalendarHttpError'
    );
  }
}

export class CalendarServerUnavailableError extends ViewBackedAppServerUnavailableError {
  constructor(message = 'Workspace server is not available') {
    super(message, 'CalendarServerUnavailableError');
  }
}

export class CalendarEventContractUnavailableError extends Error {
  constructor(message = 'Calendar event contract V2 is unavailable') {
    super(message);
    this.name = 'CalendarEventContractUnavailableError';
  }
}

export interface CalendarMailboxLinkCreateInput {
  inboundEnabled: boolean;
  mailAccountId: string;
  outboundEnabled: boolean;
}

export class CalendarApiClientService extends Service {
  constructor(
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly workspaceService: WorkspaceService
  ) {
    super();
  }

  fetchServerJson<T>(
    workspaceId: string,
    suffix: string,
    init?: RequestInit
  ): Promise<T> {
    const server = this.workspaceServerService.server;
    if (!server) {
      throw new CalendarServerUnavailableError();
    }
    return fetchCalendarJson<T>(
      `/api/workspace/${encodeURIComponent(workspaceId)}/calendar${suffix}`,
      init,
      server.fetch.bind(server) as unknown as ViewBackedAppFetch
    );
  }

  listInvitationAssignments(
    workspaceId: string,
    mailMessageId: string
  ): Promise<CalendarInvitationAssignmentsResponse> {
    return this.fetchServerJson<CalendarInvitationAssignmentsResponse>(
      workspaceId,
      `/mail/messages/${encodeURIComponent(mailMessageId)}/invitation-assignments`
    );
  }

  assignInvitation(
    workspaceId: string,
    mailMessageId: string,
    sourceId: string
  ): Promise<{ projected: number; skipped: number }> {
    return this.fetchServerJson<{ projected: number; skipped: number }>(
      workspaceId,
      `/mail/messages/${encodeURIComponent(mailMessageId)}/invitation-assignments`,
      { body: JSON.stringify({ sourceId }), method: 'POST' }
    );
  }

  projectEvents(
    workspaceId: string,
    input: {
      mode: CalendarMode;
      sourceIds?: readonly string[];
      windowEndAt: string;
      windowStartAt: string;
    }
  ): Promise<CalendarEventsResponse> {
    const params = new URLSearchParams({
      viewMode: input.mode,
      windowEndAt: input.windowEndAt,
      windowStartAt: input.windowStartAt,
    });
    for (const sourceId of input.sourceIds ?? []) {
      params.append('sourceIds', sourceId);
    }
    return this.fetchServerJson<unknown>(
      workspaceId,
      `/events?${params.toString()}`
    ).then(decodeCalendarEventsResponseV2);
  }

  createMailboxLink(
    workspaceId: string,
    source: CalendarSourceRef,
    input: CalendarMailboxLinkCreateInput
  ): Promise<CalendarMailboxLink> {
    return this.fetchServerJson<CalendarMailboxLink>(
      workspaceId,
      calendarMailboxLinksPath(source),
      {
        body: JSON.stringify(input),
        method: 'POST',
      }
    );
  }

  updateMailboxLink(
    workspaceId: string,
    source: CalendarSourceRef,
    linkId: string,
    patch: CalendarMailboxLinkPatch
  ): Promise<CalendarMailboxLink> {
    return this.fetchServerJson<CalendarMailboxLink>(
      workspaceId,
      `${calendarMailboxLinksPath(source)}/${encodeURIComponent(linkId)}`,
      {
        body: JSON.stringify(patch),
        method: 'PATCH',
      }
    );
  }

  deleteMailboxLink(
    workspaceId: string,
    source: CalendarSourceRef,
    linkId: string
  ): Promise<{ deleted: boolean }> {
    return this.fetchServerJson<{ deleted: boolean }>(
      workspaceId,
      `${calendarMailboxLinksPath(source)}/${encodeURIComponent(linkId)}`,
      { method: 'DELETE' }
    );
  }

  listMailboxLinks(
    workspaceId: string,
    source: CalendarSourceRef
  ): Promise<CalendarMailboxLinksResponse> {
    const server = this.workspaceServerService.server;
    const workspace = this.workspaceService.workspace;
    const actorUserId = server?.account$.value?.id;
    if (!server || !actorUserId || workspace.id !== workspaceId) {
      return Promise.reject(new CalendarServerUnavailableError());
    }
    return loadCalendarMailboxLinks(
      workspace.docCollection,
      { ...source, actorUserId, workspaceId },
      server.fetch.bind(server) as typeof fetch
    );
  }

  previewMailboxAreaTransition(
    workspaceId: string,
    source: CalendarSourceRef,
    linkId: string,
    input: {
      resource: CalendarMailboxAreaTransitionResource;
      targetAreaRootFolderId: string;
    }
  ): Promise<CalendarMailboxAreaTransitionPreview> {
    return this.fetchServerJson<CalendarMailboxAreaTransitionPreview>(
      workspaceId,
      `${calendarMailboxLinksPath(source)}/${encodeURIComponent(
        linkId
      )}/area-transition/preview`,
      { body: JSON.stringify(input), method: 'POST' }
    );
  }

  previewCalendarAreaTransition(
    workspaceId: string,
    source: CalendarSourceRef,
    targetAreaRootFolderId: string
  ): Promise<CalendarMailboxAreaTransitionPreview> {
    return this.fetchServerJson<CalendarMailboxAreaTransitionPreview>(
      workspaceId,
      `${calendarViewPath(source)}/area-transition/preview`,
      {
        body: JSON.stringify({ targetAreaRootFolderId }),
        method: 'POST',
      }
    );
  }

  transitionMailboxArea(
    workspaceId: string,
    source: CalendarSourceRef,
    linkId: string,
    input: {
      action: CalendarMailboxAreaTransitionAction;
      resource: CalendarMailboxAreaTransitionResource;
      targetAreaRootFolderId: string;
    }
  ): Promise<CalendarMailboxAreaTransitionResult> {
    return this.fetchServerJson<CalendarMailboxAreaTransitionResult>(
      workspaceId,
      `${calendarMailboxLinksPath(source)}/${encodeURIComponent(
        linkId
      )}/area-transition`,
      { body: JSON.stringify(input), method: 'POST' }
    );
  }

  transitionCalendarArea(
    workspaceId: string,
    source: CalendarSourceRef,
    input: {
      action: CalendarMailboxAreaTransitionAction;
      targetAreaRootFolderId: string;
    }
  ): Promise<CalendarMailboxAreaTransitionResult> {
    return this.fetchServerJson<CalendarMailboxAreaTransitionResult>(
      workspaceId,
      `${calendarViewPath(source)}/area-transition`,
      { body: JSON.stringify(input), method: 'POST' }
    );
  }

  previewMailboxAccountAreaTransition(
    workspaceId: string,
    mailAccountId: string,
    targetAreaRootFolderId: string
  ): Promise<CalendarMailboxAreaTransitionPreview | null> {
    return this.fetchServerJson<{
      preview: CalendarMailboxAreaTransitionPreview | null;
    }>(
      workspaceId,
      `/mailbox-accounts/${encodeURIComponent(mailAccountId)}/area-transition/preview`,
      {
        body: JSON.stringify({ targetAreaRootFolderId }),
        method: 'POST',
      }
    ).then(response => response.preview);
  }

  transitionMailboxAccountArea(
    workspaceId: string,
    mailAccountId: string,
    input: {
      action: CalendarMailboxAreaTransitionAction;
      targetAreaRootFolderId: string;
    }
  ): Promise<CalendarMailboxAreaTransitionResult> {
    return this.fetchServerJson<CalendarMailboxAreaTransitionResult>(
      workspaceId,
      `/mailbox-accounts/${encodeURIComponent(mailAccountId)}/area-transition`,
      { body: JSON.stringify(input), method: 'POST' }
    );
  }

  respondToInvitation(
    workspaceId: string,
    source: CalendarSourceRef,
    rowId: string,
    input: { requestId: string; status: CalendarInvitationResponseStatus }
  ): Promise<CalendarInvitationResponseResult> {
    return this.fetchServerJson<CalendarInvitationResponseResult>(
      workspaceId,
      `${calendarViewPath(source)}/rows/${encodeURIComponent(rowId)}/invitation-response`,
      {
        body: JSON.stringify(input),
        method: 'POST',
      }
    );
  }

  reconcileEvent(
    workspaceId: string,
    input: {
      columns: CalendarReconcileColumns;
      databaseId: string;
      docId: string;
      previousEvent: CalendarReconcileSnapshot | null;
      requestId: string;
      rowId: string;
      skipMail: boolean;
      syncDocIds: readonly string[];
      viewId: string;
    }
  ): Promise<{ idempotencyKey: string }> {
    const reminderWindowStartAt = new Date();
    const reminderWindowEndAt = new Date(reminderWindowStartAt);
    reminderWindowEndAt.setUTCDate(reminderWindowEndAt.getUTCDate() + 395);
    return this.fetchServerJson<{ idempotencyKey: string }>(
      workspaceId,
      `/docs/${encodeURIComponent(input.docId)}/databases/${encodeURIComponent(input.databaseId)}/rows/${encodeURIComponent(input.rowId)}/reconcile`,
      {
        body: JSON.stringify({
          columns: input.columns,
          previousEvent: input.previousEvent,
          reminderWindowEndAt: reminderWindowEndAt.toISOString(),
          reminderWindowStartAt: reminderWindowStartAt.toISOString(),
          requestId: input.requestId,
          skipMail: input.skipMail,
          syncDocIds: input.syncDocIds.filter(docId => docId !== workspaceId),
          viewId: input.viewId,
        }),
        method: 'POST',
      }
    );
  }
}

const EVENT_KEYS = [
  'allDay',
  'attendeeCount',
  'canEdit',
  'color',
  'endAt',
  'icsSequence',
  'icsUid',
  'id',
  'invitationMethod',
  'invitationState',
  'lifecycleState',
  'physicalLocation',
  'rowId',
  'sourceId',
  'sourceMailAccountId',
  'sourceMailAttachmentBlobKey',
  'sourceMailMessageId',
  'sourceTitle',
  'sourceType',
  'startAt',
  'status',
  'timezone',
  'title',
  'virtualLocation',
] as const;
const SOURCE_KEYS = [
  'canEdit',
  'canManage',
  'color',
  'error',
  'id',
  'lifecycleState',
  'title',
  'visible',
] as const;
const RESPONSE_KEYS = [
  'calendarEventContractVersion',
  'events',
  'sources',
  'window',
] as const;
const WINDOW_KEYS = ['endAt', 'mode', 'startAt'] as const;

export function decodeCalendarEventsResponseV2(
  value: unknown
): CalendarEventsResponse {
  const response = exactRecord(value, RESPONSE_KEYS);
  if (response.calendarEventContractVersion !== 2) invalidCalendarEventWire();
  if (!Array.isArray(response.events) || !Array.isArray(response.sources)) {
    invalidCalendarEventWire();
  }
  const events = response.events.map(value => {
    const event = exactRecord(value, EVENT_KEYS) as Record<string, unknown>;
    assertEventWire(event);
    const { sourceTitle: _sourceTitle, ...domainEvent } =
      event as unknown as CalendarProjectedEventWire;
    return domainEvent;
  });
  const sources = response.sources.map(value => {
    const source = exactRecord(value, SOURCE_KEYS);
    assertBoolean(source.canEdit);
    assertBoolean(source.canManage);
    assertNullableString(source.color);
    assertNullableString(source.error);
    assertString(source.id);
    assertLifecycle(source.lifecycleState);
    assertString(source.title);
    assertBoolean(source.visible);
    return source as unknown as CalendarEventsResponse['sources'][number];
  });
  const window = exactRecord(response.window, WINDOW_KEYS);
  assertString(window.endAt);
  assertString(window.startAt);
  if (!['day', 'week', 'month'].includes(window.mode as string)) {
    invalidCalendarEventWire();
  }
  return {
    calendarEventContractVersion: 2,
    events,
    sources,
    window: window as unknown as CalendarEventsResponse['window'],
  };
}

function assertEventWire(event: Record<string, unknown>): void {
  assertBoolean(event.allDay);
  if (!Number.isInteger(event.attendeeCount) || Number(event.attendeeCount) < 0)
    invalidCalendarEventWire();
  assertBoolean(event.canEdit);
  assertNullableString(event.color);
  for (const key of [
    'endAt',
    'id',
    'rowId',
    'sourceId',
    'sourceTitle',
    'startAt',
    'timezone',
    'title',
  ])
    assertString(event[key]);
  if (event.icsSequence !== null && !Number.isInteger(event.icsSequence))
    invalidCalendarEventWire();
  for (const key of [
    'icsUid',
    'invitationMethod',
    'invitationState',
    'physicalLocation',
    'sourceMailAccountId',
    'sourceMailAttachmentBlobKey',
    'sourceMailMessageId',
    'sourceType',
    'status',
    'virtualLocation',
  ])
    assertNullableString(event[key]);
  assertLifecycle(event.lifecycleState);
}

function exactRecord(
  value: unknown,
  keys: readonly string[]
): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    return invalidCalendarEventWire();
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  if (
    actual.length !== expected.length ||
    actual.some((key, i) => key !== expected[i])
  )
    return invalidCalendarEventWire();
  return value as Record<string, unknown>;
}

function assertBoolean(value: unknown): asserts value is boolean {
  if (typeof value !== 'boolean') invalidCalendarEventWire();
}

function assertString(value: unknown): asserts value is string {
  if (typeof value !== 'string') invalidCalendarEventWire();
}

function assertNullableString(value: unknown): asserts value is string | null {
  if (value !== null && typeof value !== 'string') invalidCalendarEventWire();
}

function assertLifecycle(value: unknown): void {
  if (!['active', 'deleted', 'trashed'].includes(value as string))
    invalidCalendarEventWire();
}

function invalidCalendarEventWire(): never {
  throw new CalendarEventContractUnavailableError();
}

export async function fetchCalendarJson<T>(
  url: string,
  init: RequestInit | undefined,
  fetcher: ViewBackedAppFetch
): Promise<T> {
  try {
    return await fetchViewBackedAppJson<T>(
      url,
      init,
      fetcher,
      (status, statusText, message) =>
        new CalendarHttpError(status, statusText, message)
    );
  } catch (error) {
    if (error instanceof SyntaxError) {
      throw new CalendarServerUnavailableError(
        'Workspace server returned an empty or invalid response'
      );
    }
    throw error;
  }
}

export function calendarApiUrl(workspaceId: string, suffix: string): string {
  return `/api/workspace/${encodeURIComponent(workspaceId)}/calendar${suffix}`;
}

export function calendarMailboxLinksPath(source: CalendarSourceRef): string {
  return `${calendarViewPath(source)}/mailbox-links`;
}

function calendarViewPath(source: CalendarSourceRef): string {
  return `/docs/${encodeURIComponent(source.docId)}/databases/${encodeURIComponent(source.databaseId)}/views/${encodeURIComponent(source.viewId)}`;
}

export function calendarMailboxLinksUrl(
  apiBase: string,
  source: CalendarSourceRef
): string {
  return `${apiBase}${calendarMailboxLinksPath(source)}`;
}

export function calendarResourceId(
  docId: string,
  databaseId: string,
  viewId: string
): string {
  return viewBackedAppResourceId(
    CALENDAR_VIEW_BACKED_APP_DESCRIPTOR,
    docId,
    databaseId,
    viewId
  );
}

export function isCalendarCacheFallbackError(error: unknown): boolean {
  const status = calendarErrorStatus(error);
  if (status > 0) {
    return status === 404 || status >= 500;
  }
  return (
    error instanceof CalendarServerUnavailableError ||
    error instanceof TypeError
  );
}

export function formatCalendarRequestError(
  error: unknown,
  text: (
    key: string,
    fallback: string,
    params?: Record<string, string>
  ) => string
): string {
  if (!error) return '';
  const status = calendarErrorStatus(error);
  if (status > 0) {
    if (status === 401 || status === 403) {
      return text(
        'com.streamlain.calendarApp.error.permission',
        'You do not have access to this calendar data, or your session expired.'
      );
    }
    if (status === 409) {
      return text(
        'com.streamlain.calendarApp.error.conflict',
        'The calendar structure is inconsistent. Reload this view and try again.'
      );
    }
    if (status >= 500) {
      return text(
        'com.streamlain.calendarApp.error.server',
        'The server could not load the calendar data right now. Try again in a moment.'
      );
    }
    return text(
      'com.streamlain.calendarApp.error.http',
      'Calendar data could not be loaded. Try again.'
    );
  }
  if (
    error instanceof CalendarServerUnavailableError ||
    error instanceof TypeError
  ) {
    return text(
      'com.streamlain.calendarApp.error.network',
      `${BRAND_DISPLAY_NAME} cannot be reached right now. Check your connection and try again.`
    );
  }
  return text(
    'com.streamlain.calendarApp.loadFailedHint',
    'Local calendar documents remain available. Reload the overview or try again shortly.'
  );
}

function calendarErrorStatus(error: unknown): number {
  if (!error || typeof error !== 'object') {
    return 0;
  }
  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : 0;
}
